import React, { useState } from "react";
import { ShieldCheck, Lock, Check, X, UserCheck } from "lucide-react";
import { ROLES } from "../../app/context/AuthContext";
import { useNotification } from "../../app/context/NotificationContext";

export function AccessControlModule() {
  const { addToast } = useNotification();

  const modulesList = [
    { key: "dashboard", name: "Dashboard & Executive KPIs" },
    { key: "procurement", name: "Procurement & Purchase Orders" },
    { key: "receiving", name: "Receiving & GRN Putaway" },
    { key: "inventory", name: "Inventory & Stock Reconciliation" },
    { key: "warehouse", name: "Warehouse & Bin Location Maps" },
    { key: "stockTransfer", name: "Inter-Warehouse Transfers" },
    { key: "sales", name: "Retail POS & Wholesale Desk" },
    { key: "customers", name: "Customer Credit Accounts" },
    { key: "suppliers", name: "Suppliers & Vendor Master" },
    { key: "finance", name: "Finance, P&L & Settlements" },
    { key: "employees", name: "Employees & Payroll" },
    { key: "batchExpiry", name: "Batch Expiry Radar" },
    { key: "deadStock", name: "Dead Stock & Smart Reorder" },
    { key: "returns", name: "Returns & Damage Write-Offs" },
    { key: "analytics", name: "Reports & Intelligence Engine" },
    { key: "auditLog", name: "Audit Trail & Activity Log" },
    { key: "settings", name: "System Settings & Tax Slabs" }
  ];

  // RBAC Matrix State
  const [matrix, setMatrix] = useState({
    [ROLES.OWNER]: modulesList.reduce((acc, m) => ({ ...acc, [m.key]: { view: true, create: true, edit: true, delete: true } }), {}),
    [ROLES.MANAGER]: modulesList.reduce((acc, m) => ({
      ...acc,
      [m.key]: {
        view: true,
        create: !["employees", "accessControl"].includes(m.key),
        edit: !["employees"].includes(m.key),
        delete: false
      }
    }), {}),
    [ROLES.WAREHOUSE]: modulesList.reduce((acc, m) => ({
      ...acc,
      [m.key]: {
        view: ["dashboard", "receiving", "inventory", "warehouse", "stockTransfer", "batchExpiry", "returns"].includes(m.key),
        create: ["receiving", "stockTransfer"].includes(m.key),
        edit: ["inventory", "warehouse"].includes(m.key),
        delete: false
      }
    }), {}),
    [ROLES.SALES]: modulesList.reduce((acc, m) => ({
      ...acc,
      [m.key]: {
        view: ["dashboard", "sales", "inventory", "customers", "analytics"].includes(m.key),
        create: ["sales", "customers"].includes(m.key),
        edit: false,
        delete: false
      }
    }), {}),
    [ROLES.FINANCE]: modulesList.reduce((acc, m) => ({
      ...acc,
      [m.key]: {
        view: ["dashboard", "finance", "customers", "suppliers", "employees", "analytics", "auditLog"].includes(m.key),
        create: ["finance"].includes(m.key),
        edit: ["finance"].includes(m.key),
        delete: false
      }
    }), {}),
    [ROLES.RETAIL]: modulesList.reduce((acc, m) => ({
      ...acc,
      [m.key]: {
        view: ["dashboard", "sales", "inventory"].includes(m.key),
        create: ["sales", "returns"].includes(m.key),
        edit: false,
        delete: false
      }
    }), {})
  });

  const [selectedRole, setSelectedRole] = useState(ROLES.MANAGER);

  const togglePerm = (moduleKey, permType) => {
    setMatrix((prev) => ({
      ...prev,
      [selectedRole]: {
        ...prev[selectedRole],
        [moduleKey]: {
          ...prev[selectedRole][moduleKey],
          [permType]: !prev[selectedRole][moduleKey]?.[permType]
        }
      }
    }));
  };

  const handleSave = () => {
    addToast(`RBAC permissions matrix updated for ${selectedRole}!`, "success");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-500" />
            Role-Based Access Control (RBAC) Permission Matrix
          </h2>
          <p className="text-xs text-slate-500">
            Granular View / Create / Edit / Delete permissions per module for each of the 6 enterprise roles
          </p>
        </div>

        <button
          onClick={handleSave}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-600/30 transition"
        >
          Save Permissions Matrix
        </button>
      </div>

      {/* Role Picker Pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-slate-200 dark:border-slate-800 text-xs">
        {Object.values(ROLES).map((r) => (
          <button
            key={r}
            onClick={() => setSelectedRole(r)}
            className={`px-3.5 py-1.5 rounded-xl font-semibold whitespace-nowrap transition ${
              selectedRole === r
                ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                : "bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-200"
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      {/* Permission Matrix Grid */}
      <div className="rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 text-slate-500 uppercase font-semibold text-[10px]">
              <th className="py-3 px-4">Module Name</th>
              <th className="py-3 px-4 text-center w-24">View</th>
              <th className="py-3 px-4 text-center w-24">Create</th>
              <th className="py-3 px-4 text-center w-24">Edit</th>
              <th className="py-3 px-4 text-center w-24">Delete</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {modulesList.map((m) => {
              const perms = matrix[selectedRole]?.[m.key] || {};
              return (
                <tr key={m.key} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40">
                  <td className="py-3 px-4 font-semibold text-slate-900 dark:text-white">
                    {m.name}
                  </td>
                  {["view", "create", "edit", "delete"].map((type) => (
                    <td key={type} className="py-3 px-4 text-center">
                      <button
                        onClick={() => togglePerm(m.key, type)}
                        className={`w-6 h-6 rounded-lg inline-flex items-center justify-center transition ${
                          perms[type]
                            ? "bg-emerald-600 text-white shadow-xs"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-400 hover:bg-slate-200"
                        }`}
                      >
                        {perms[type] ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
